import React from "react";
import "./dashboardWidget.css";

export const SNAPSHOT_CARD_STYLE = {
  background: "var(--nx-surface)",
  border: "1px solid var(--nx-border)",
  borderRadius: "20px",
  padding: "20px 20px 18px",
};

export const SNAPSHOT_CARD_TITLE_STYLE = {
  fontSize: "0.82rem",
  fontWeight: 800,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: "var(--nx-gold-ink, #8a6d23)",
  marginBottom: "4px",
};

const CARD_SUBTITLE_STYLE = {
  fontSize: "12px",
  fontWeight: 600,
  letterSpacing: "0.04em",
  color: "var(--nx-text-3, #94a0b2)",
  marginBottom: "14px",
};

const ROW_STYLE = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "10px 4px",
};

const ROW_LABEL_STYLE = {
  fontSize: "14px",
  fontWeight: 700,
  letterSpacing: "0.02em",
};

const ROW_SUBTITLE_STYLE = {
  display: "block",
  fontSize: "11px",
  fontWeight: 500,
  color: "var(--nx-text-3, #94a0b2)",
  marginTop: "2px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const ROW_DOTS_STYLE = {};

const ROW_VALUE_STYLE = {
  minWidth: "72px",
  flexShrink: 0,
  textAlign: "right",
  fontSize: "16px",
  fontWeight: 800,
  color: "var(--nx-text, #1a2233)",
};

const CLICKABLE_ROW_STYLE = {
  ...ROW_STYLE,
  width: "100%",
  border: "none",
  background: "transparent",
  padding: "10px 4px",
  margin: 0,
  font: "inherit",
  color: "inherit",
  textAlign: "left",
  borderRadius: "10px",
};

function renderRowContent({ label, subtitle, value, valueNode }) {
  const trailingAlignStyle = subtitle ? { alignSelf: "center" } : undefined;

  return (
    <>
      <span className="kpi-snapshot-row__label" style={ROW_LABEL_STYLE}>
        {label}
        {subtitle ? <span style={ROW_SUBTITLE_STYLE}>{subtitle}</span> : null}
      </span>
      <span
        aria-hidden="true"
        className="kpi-snapshot-row__dots"
        style={{ ...ROW_DOTS_STYLE, ...trailingAlignStyle }}
      />
      {valueNode ? (
        <span style={trailingAlignStyle}>{valueNode}</span>
      ) : (
        <span style={{ ...ROW_VALUE_STYLE, ...trailingAlignStyle }}>
          {value}
        </span>
      )}
    </>
  );
}

export function MetricRow({ label, value, subtitle, valueNode, onClick }) {
  const rowAlignStyle = subtitle
    ? { ...ROW_STYLE, alignItems: "flex-start" }
    : ROW_STYLE;
  const clickableAlignStyle = subtitle
    ? { ...CLICKABLE_ROW_STYLE, alignItems: "flex-start" }
    : CLICKABLE_ROW_STYLE;

  if (onClick) {
    return (
      <button
        type="button"
        className="kpi-snapshot-row kpi-snapshot-row--clickable"
        style={clickableAlignStyle}
        onClick={onClick}
      >
        {renderRowContent({ label, subtitle, value, valueNode })}
      </button>
    );
  }

  return (
    <div className="kpi-snapshot-row" style={rowAlignStyle}>
      {renderRowContent({ label, subtitle, value, valueNode })}
    </div>
  );
}

function handleTitleKeyDown(event, onTitleClick) {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    onTitleClick();
  }
}

export function SnapshotMetricCard({
  title,
  subtitle,
  metrics,
  emptyMessage,
  onTitleClick,
  leadingContent = null,
}) {
  return (
    <div className="kpi-snapshot-card dashboard-widget-card">
      <div
        className="dashboard-widget-title"
        {...(onTitleClick
          ? {
              role: "button",
              tabIndex: 0,
              onClick: onTitleClick,
              onKeyDown: (event) => handleTitleKeyDown(event, onTitleClick),
            }
          : {})}
      >
        {title}
      </div>
      {subtitle ? (
        <div className="dashboard-widget-subtitle" style={CARD_SUBTITLE_STYLE}>
          {subtitle}
        </div>
      ) : null}
      {metrics.length === 0 && emptyMessage ? (
        <p className="dashboard-widget-empty">{emptyMessage}</p>
      ) : (
        <>
          {leadingContent ? (
            <div className="dashboard-widget-body">{leadingContent}</div>
          ) : null}
          <div className="dashboard-widget-rows">
            {metrics.map((metric, index) => (
              <MetricRow
                key={metric.key || `${metric.label}-${index}`}
                label={metric.label}
                subtitle={metric.subtitle}
                value={metric.value}
                valueNode={metric.valueNode}
                onClick={metric.onClick}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
